const DATABASE_NAME = 'jurytracker-juror-sync';
const DATABASE_VERSION = 1;
const MUTATION_STORE = 'mutations';
const DRAFT_STORE = 'drafts';
export const JUROR_OUTBOX_CHANGE_EVENT = 'jurytracker:juror-outbox-change';

const notifyOutboxChange = () => {
  if (typeof window !== 'undefined' && typeof window.dispatchEvent === 'function') {
    window.dispatchEvent(new Event(JUROR_OUTBOX_CHANGE_EVENT));
  }
};

const clone = (value) => {
  if (value == null) return value;
  if (typeof structuredClone === 'function') {
    return structuredClone(value);
  }
  return JSON.parse(JSON.stringify(value));
};

const asPromise = (request) => new Promise((resolve, reject) => {
  request.onsuccess = () => resolve(request.result);
  request.onerror = () => reject(request.error || new Error('IndexedDB request failed.'));
});

const waitForTransaction = (transaction) => new Promise((resolve, reject) => {
  transaction.oncomplete = () => resolve();
  transaction.onabort = () => reject(transaction.error || new Error('IndexedDB transaction aborted.'));
  transaction.onerror = () => reject(transaction.error || new Error('IndexedDB transaction failed.'));
});

const normalizeIdentifier = (value) => String(value ?? '').trim();

export const createDraftKey = ({ panelId, jurorId, actorId }) => {
  const parts = [panelId, jurorId, actorId].map(normalizeIdentifier);
  if (parts.some((part) => !part)) {
    return null;
  }
  return JSON.stringify(parts);
};

const openDatabase = (indexedDb, databaseName) => new Promise((resolve, reject) => {
  const request = indexedDb.open(databaseName, DATABASE_VERSION);
  request.onupgradeneeded = () => {
    const database = request.result;
    if (!database.objectStoreNames.contains(MUTATION_STORE)) {
      const mutations = database.createObjectStore(MUTATION_STORE, { keyPath: 'mutationId' });
      mutations.createIndex('actorId', 'actorId', { unique: false });
      mutations.createIndex('panelId', 'panelId', { unique: false });
      mutations.createIndex('state', 'state', { unique: false });
    }
    if (!database.objectStoreNames.contains(DRAFT_STORE)) {
      const drafts = database.createObjectStore(DRAFT_STORE, { keyPath: 'draftKey' });
      drafts.createIndex('actorId', 'actorId', { unique: false });
      drafts.createIndex('panelId', 'panelId', { unique: false });
    }
  };
  request.onsuccess = () => resolve(request.result);
  request.onerror = () => reject(request.error || new Error('Unable to open the juror outbox.'));
  request.onblocked = () => reject(new Error('The juror outbox upgrade is blocked by another tab.'));
});

class MemoryJurorOutbox {
  constructor() {
    this.mutations = new Map();
    this.drafts = new Map();
  }

  async putMutation(mutation) {
    this.mutations.set(mutation.mutationId, clone(mutation));
    return clone(mutation);
  }

  async getMutation(mutationId) {
    return clone(this.mutations.get(mutationId) || null);
  }

  async listMutations(filters = {}) {
    return [...this.mutations.values()]
      .filter((mutation) => Object.entries(filters).every(
        ([key, value]) => value == null || mutation[key] === value
      ))
      .sort((left, right) => String(left.createdAt).localeCompare(String(right.createdAt)))
      .map(clone);
  }

  async removeMutation(mutationId) {
    this.mutations.delete(mutationId);
  }

  async putDraft(draft) {
    this.drafts.set(draft.draftKey, clone(draft));
    return clone(draft);
  }

  async getDraft(draftKey) {
    return clone(this.drafts.get(draftKey) || null);
  }

  async listDrafts(filters = {}) {
    return [...this.drafts.values()]
      .filter((draft) => Object.entries(filters).every(
        ([key, value]) => value == null || draft[key] === value
      ))
      .sort((left, right) => String(right.updatedAt).localeCompare(String(left.updatedAt)))
      .map(clone);
  }

  async removeDraft(draftKey) {
    this.drafts.delete(draftKey);
  }

  async clearForActor(actorId) {
    for (const [key, mutation] of this.mutations) {
      if (mutation.actorId === actorId) this.mutations.delete(key);
    }
    for (const [key, draft] of this.drafts) {
      if (draft.actorId === actorId) this.drafts.delete(key);
    }
  }

  async clearAll() {
    this.mutations.clear();
    this.drafts.clear();
  }
}

/**
 * Durable storage for acknowledged-write state. When IndexedDB is unavailable
 * (SSR, private browsing restrictions, or unit tests), the same API falls back
 * to an in-memory store instead of preventing courtroom edits.
 */
export class JurorOutbox {
  constructor({ indexedDB = globalThis.indexedDB, databaseName = DATABASE_NAME } = {}) {
    this.indexedDB = indexedDB;
    this.databaseName = databaseName;
    this.memory = new MemoryJurorOutbox();
    this.databasePromise = null;
    this.usingMemoryFallback = !indexedDB;
  }

  async getDatabase() {
    if (this.usingMemoryFallback) return null;
    if (!this.databasePromise) {
      this.databasePromise = openDatabase(this.indexedDB, this.databaseName).catch(() => {
        this.usingMemoryFallback = true;
        return null;
      });
    }
    return this.databasePromise;
  }

  async run(storeName, mode, operation, fallbackOperation) {
    const database = await this.getDatabase();
    if (!database) {
      return fallbackOperation();
    }

    try {
      const transaction = database.transaction(storeName, mode);
      const store = transaction.objectStore(storeName);
      const result = await operation(store);
      await waitForTransaction(transaction);
      return result;
    } catch (error) {
      // A browser may revoke IndexedDB access after startup. Preserve usability
      // for the current tab and let the UI clearly retain an unsynced draft.
      this.usingMemoryFallback = true;
      return fallbackOperation();
    }
  }

  async putMutation(mutation) {
    if (!mutation?.mutationId) throw new TypeError('mutationId is required.');
    const record = { ...clone(mutation), updatedAt: new Date().toISOString() };
    const result = await this.run(
      MUTATION_STORE,
      'readwrite',
      async (store) => {
        await asPromise(store.put(record));
        return clone(record);
      },
      () => this.memory.putMutation(record)
    );
    notifyOutboxChange();
    return result;
  }

  async updateMutation(mutationId, patch) {
    const current = await this.getMutation(mutationId);
    if (!current) return null;
    return this.putMutation({ ...current, ...clone(patch), mutationId });
  }

  async getMutation(mutationId) {
    return this.run(
      MUTATION_STORE,
      'readonly',
      async (store) => clone((await asPromise(store.get(mutationId))) || null),
      () => this.memory.getMutation(mutationId)
    );
  }

  async listMutations(filters = {}) {
    return this.run(
      MUTATION_STORE,
      'readonly',
      async (store) => {
        const records = await asPromise(store.getAll());
        return records
          .filter((mutation) => Object.entries(filters).every(
            ([key, value]) => value == null || mutation[key] === value
          ))
          .sort((left, right) => String(left.createdAt).localeCompare(String(right.createdAt)))
          .map(clone);
      },
      () => this.memory.listMutations(filters)
    );
  }

  async removeMutation(mutationId) {
    const result = await this.run(
      MUTATION_STORE,
      'readwrite',
      async (store) => {
        await asPromise(store.delete(mutationId));
      },
      () => this.memory.removeMutation(mutationId)
    );
    notifyOutboxChange();
    return result;
  }

  async putDraft({
    draftKey,
    panelId,
    jurorId,
    actorId,
    value,
    baseValue = '',
    baseRevision = 0,
    state = 'local-draft',
    conflict = null,
  }) {
    const resolvedKey = draftKey || createDraftKey({ panelId, jurorId, actorId });
    if (!resolvedKey) throw new TypeError('A complete draft key is required.');
    const record = {
      draftKey: resolvedKey,
      panelId: normalizeIdentifier(panelId),
      jurorId: normalizeIdentifier(jurorId),
      actorId: normalizeIdentifier(actorId),
      value: String(value ?? ''),
      baseValue: String(baseValue ?? ''),
      baseRevision: Math.max(0, Number(baseRevision) || 0),
      state,
      conflict: conflict ? clone(conflict) : null,
      updatedAt: new Date().toISOString(),
    };
    const result = await this.run(
      DRAFT_STORE,
      'readwrite',
      async (store) => {
        await asPromise(store.put(record));
        return clone(record);
      },
      () => this.memory.putDraft(record)
    );
    notifyOutboxChange();
    return result;
  }

  async getDraft(draftKey) {
    if (!draftKey) return null;
    return this.run(
      DRAFT_STORE,
      'readonly',
      async (store) => clone((await asPromise(store.get(draftKey))) || null),
      () => this.memory.getDraft(draftKey)
    );
  }

  async listDrafts(filters = {}) {
    return this.run(
      DRAFT_STORE,
      'readonly',
      async (store) => {
        const records = await asPromise(store.getAll());
        return records
          .filter((draft) => Object.entries(filters).every(
            ([key, value]) => value == null || draft[key] === value
          ))
          .sort((left, right) => String(right.updatedAt).localeCompare(String(left.updatedAt)))
          .map(clone);
      },
      () => this.memory.listDrafts(filters)
    );
  }

  async removeDraft(draftKey) {
    if (!draftKey) return;
    const result = await this.run(
      DRAFT_STORE,
      'readwrite',
      async (store) => {
        await asPromise(store.delete(draftKey));
      },
      () => this.memory.removeDraft(draftKey)
    );
    notifyOutboxChange();
    return result;
  }

  async clearForActor(actorId) {
    const [mutations, drafts] = await Promise.all([
      this.listMutations({ actorId }),
      this.listDrafts({ actorId }),
    ]);
    await Promise.all([
      ...mutations.map((mutation) => this.removeMutation(mutation.mutationId)),
      ...drafts.map((draft) => this.removeDraft(draft.draftKey)),
    ]);
    notifyOutboxChange();
  }

  async clearAll() {
    const database = await this.getDatabase();
    if (!database) {
      await this.memory.clearAll();
      notifyOutboxChange();
      return;
    }
    try {
      const transaction = database.transaction([MUTATION_STORE, DRAFT_STORE], 'readwrite');
      transaction.objectStore(MUTATION_STORE).clear();
      transaction.objectStore(DRAFT_STORE).clear();
      await waitForTransaction(transaction);
      await this.memory.clearAll();
      notifyOutboxChange();
    } catch (error) {
      this.usingMemoryFallback = true;
      await this.memory.clearAll();
      notifyOutboxChange();
    }
  }
}

export const createJurorOutbox = (options) => new JurorOutbox(options);
export const jurorOutbox = createJurorOutbox();

export default jurorOutbox;
